/**
 * @module routes.Sessions.AllCoachesPublicSession._components.useFacilityAvailability
 */
import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

import { bookableRates } from "./coachMeta";
import {
  FacilitySlot,
  aggregateSlots,
  unionAvailableDates,
} from "./byDateAvailability";

/**
 * The rate a coach's availability is computed against.
 *
 * IMPORTANT LIMITATION: both scheduling endpoints answer per coach *and per
 * booking option* — they need a `lesson_rate_id` and `slot_duration`. The By
 * Date screen asks a question neither endpoint answers directly ("when is
 * anyone at this facility free?"). The Facility Calendar asks each coach about
 * their default option and unions the answers, so a coach who is free only for
 * another option is missed there. The Dates list asks about every option
 * (`availabilityRates`), one request per coach and option, so the options it
 * lists under a time are the options that offered it. A facility-level
 * availability endpoint would remove the extra requests — see the PR notes.
 */
export const availabilityRate = (
  coach: CoachProfileModel,
  durationMinutes?: number | null,
) => {
  const rates = bookableRates(coach);

  /**
   * CFTB-3435: with a duration filter applied, ask about a rate of that length
   * rather than the coach's default. The endpoints answer per booking option, so
   * the length asked for is the length the answer describes — asking about a
   * 60-minute default and drawing the result under a "30 mins" filter showed slots
   * the coach cannot actually be booked into.
   *
   * A coach with no rate of that length returns null and is not asked at all: they
   * have no availability for it, and a request would only answer for some other
   * length.
   */
  if (durationMinutes) {
    return (
      rates.find((rate) => rate.durationInMinutes === durationMinutes) || null
    );
  }

  return rates.find((rate) => rate.defaultRate) || rates[0] || null;
};

/**
 * Every rate a coach's availability is asked for when the caller asks per
 * booking option: each payable rate, or each payable rate of the filtered
 * length.
 *
 * The answer for one option is not the answer for another. An option whose
 * every bay is taken is not free when the coach is, and a group option with a
 * seat left is free when the coach's other options are not. The Dates list
 * lists options under a time, so it asks this way.
 */
export const availabilityRates = (
  coach: CoachProfileModel,
  durationMinutes?: number | null,
) =>
  bookableRates(coach).filter(
    (rate) => !durationMinutes || rate.durationInMinutes === durationMinutes,
  );

interface DayAvailability {
  /** ISO dates anyone at the facility can be booked on, for the loaded window. */
  availableDates: Set<string>;
  datesLoading: boolean;
  /** Facility-wide slots for `date`, with the coaches free at each. */
  slots: FacilitySlot[];
  /**
   * The same day, still split per coach — what the Facility Calendar's columns
   * are. By Date wants them collapsed, the calendar grid wants them apart, so
   * both shapes come back rather than one being rebuilt from the other.
   */
  slotsByCoach: PerCoachSlots[];
  slotsLoading: boolean;
}

export interface PerCoachSlots {
  coachId: string;
  slots: AvailableSlotModel[];
}

/**
 * One row per coach, with the slots of every rate asked about that coach.
 * With one rate per coach this is the answer as it came back.
 */
const slotsPerCoach = (
  perRate: Array<{ coachId: string; slots: AvailableSlotModel[] }>,
): PerCoachSlots[] => {
  const byCoach = new Map<string, AvailableSlotModel[]>();
  perRate.forEach(({ coachId, slots }) => {
    byCoach.set(coachId, [...(byCoach.get(coachId) || []), ...slots]);
  });
  return Array.from(byCoach.entries()).map(([coachId, slots]) => ({
    coachId,
    slots,
  }));
};

/**
 * Availability for the facility's By Date view: the bookable days around
 * `month`, and the slots on `date`.
 */
const useFacilityAvailability = (
  coaches: CoachProfileModel[],
  month: DateTime,
  date: string | null,
  /**
   * Which scheduling surface to ask: the public flow gets /api/public, the in-app
   * flow /api/vN. Getting the coach's identifier wrong on either returns no
   * availability at all rather than an error, and the two endpoints do not agree
   * on it — see below.
   */
  isPublicPage = true,
  /**
   * The length the screen is filtered to, or null for "any". Part of what is asked
   * of the endpoints, so changing it reloads both the month and the day.
   */
  durationMinutes: number | null = null,
  /**
   * Ask about each booking option (`availabilityRates`) instead of one rate per
   * coach (`availabilityRate`). The slots then record which options offered
   * each time. The Dates list needs this, because it lists options under a
   * time. The Facility Calendar keeps one rate per coach.
   */
  askEveryRate = false,
): DayAvailability => {
  /**
   * `/available_dates` looks a coach up by their *user's* external id on both
   * surfaces, and `coach.id` is that id in both payloads.
   */
  const coachDatesId = (coach: CoachProfileModel) => coach.id;
  /**
   * `/coach_profiles/:id/available_times` is the exception: the public route
   * finds the coach by `calendar_uuid`, while the in-app one uses the same
   * external id as everything else. Passing the uuid to the dates endpoint —
   * which this used to do — matched no coach, so the public By Date tab reported
   * the facility as having no availability at all.
   */
  const coachTimesId = (coach: CoachProfileModel) =>
    isPublicPage ? coach.calendarUuid : coach.id;
  /**
   * Which coaches are being asked about, not how many.
   *
   * Both effects used to depend on `coaches.length`, which holds while the set
   * changes: filtering the screen to one coach and then to another leaves the
   * length at one, so the month's dates and the day's times stayed the first
   * coach's.
   */
  const coachesKey = coaches.map((coach) => coach.id).join(",");
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [datesLoading, setDatesLoading] = useState(true);
  const [slots, setSlots] = useState<FacilitySlot[]>([]);
  const [slotsByCoach, setSlotsByCoach] = useState<PerCoachSlots[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  /** The (coach, rate) pairs asked about, for both endpoints. */
  const questions = () =>
    coaches.flatMap((coach) => {
      if (askEveryRate) {
        return availabilityRates(coach, durationMinutes).map((rate) => ({
          coach,
          rate,
        }));
      }
      const rate = availabilityRate(coach, durationMinutes);
      return rate ? [{ coach, rate }] : [];
    });

  // A month either side, so moving one month does not blank the calendar.
  const from = month.startOf("month").minus({ months: 1 }).toISODate();
  const to = month.endOf("month").plus({ months: 1 }).toISODate();

  useEffect(() => {
    let active = true;
    setDatesLoading(true);

    const requests = questions().map(({ coach, rate }) =>
      schedulingFetchGet(
        "/available_dates",
        {
          from,
          to,
          coach_profile_ids: [coachDatesId(coach)],
          slot_duration: rate.durationInMinutes,
          lesson_rate_id: rate.id,
        },
        {},
        null,
        isPublicPage,
      ).catch(() => []),
    );

    Promise.all(requests)
      .then((responses) => {
        if (!active) {
          return;
        }
        setAvailableDates(unionAvailableDates(responses.flat()));
      })
      .finally(() => {
        if (active) {
          setDatesLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, coachesKey, isPublicPage, durationMinutes, askEveryRate]);

  useEffect(() => {
    if (!date) {
      setSlots([]);
      setSlotsByCoach([]);
      return undefined;
    }

    let active = true;
    setSlotsLoading(true);

    const dayStart = DateTime.fromISO(date).startOf("day");
    const dayEnd = DateTime.fromISO(date).endOf("day");

    const requests = questions().map(({ coach, rate }) =>
      schedulingFetchGet(
        `/coach_profiles/${coachTimesId(coach)}/available_times`,
        {
          from: dayStart.toISO(),
          to: dayEnd.toISO(),
          coach_profile_ids: [coachTimesId(coach)],
          slot_duration: rate.durationInMinutes,
          lesson_rate_id: rate.id,
        },
        {},
        null,
        isPublicPage,
      )
        .then((raw: any) => ({
          coachId: coach.id,
          rateId: rate.id,
          slots: (raw?.[0]?.slots || []).map(
            (slot: any) => new AvailableSlotModel(slot),
          ) as AvailableSlotModel[],
        }))
        .catch(() => ({
          coachId: coach.id,
          rateId: rate.id,
          slots: [] as AvailableSlotModel[],
        })),
    );

    Promise.all(requests)
      .then((perRate) => {
        if (active) {
          setSlots(
            aggregateSlots(
              askEveryRate
                ? perRate
                : perRate.map(({ coachId, slots }) => ({ coachId, slots })),
            ),
          );
          setSlotsByCoach(slotsPerCoach(perRate));
        }
      })
      .finally(() => {
        if (active) {
          setSlotsLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, coachesKey, isPublicPage, durationMinutes, askEveryRate]);

  return { availableDates, datesLoading, slots, slotsByCoach, slotsLoading };
};

export default useFacilityAvailability;
